/**
 * CREATION TYPES
 * 
 * Types for the Creations feature - manis, press-ons, jewelry, etc.
 */

import { Timestamp } from 'firebase/firestore';

// Photo with default flag
export interface Photo {
  url: string;
  isDefault: boolean;
}

// Item used in creation (references collection item)
export interface ItemUsed {
  itemId: string;
  /**
   * The 8-type union (added `cuticle_care` in Phase 8 — was missing so
   * users couldn't attach cuticle oils/creams to a creation, which meant
   * the "Manis featuring this" section on cuticle care detail pages had
   * no way to be populated).
   */
  itemType:
    | 'polish'
    | 'plate'
    | 'transfer'
    | 'embellishment'
    | 'tool'
    | 'powder'
    | 'acrylic'
    | 'cuticle_care';
  name: string;
  brand: string;
  photoUrl?: string;
}

// Press-on specific details
export interface PressOnDetails {
  shapesAvailable?: string[];
  lengthsAvailable?: string[];
  customSizing?: boolean;
  sizingKitAvailable?: boolean;
  guideUrl?: string;
}

// Main creation type
export interface Creation {
  id: string;
  userId: string;
  
  // Core fields
  title: string;
  description: string;
  quantity: number;
  photos: Photo[];
  itemsUsed: ItemUsed[];
  /**
   * Denormalized query-friendly form of `itemsUsed` for the "Manis
   * featuring this" section on item detail pages. Each entry is a
   * string like `polish__mooncat__house-of-hades` (itemType +
   * slugified-brand + slugified-name). Firestore array-contains works
   * on arrays of strings but not on arrays of objects — this is the
   * strings-only shape the composite index can query.
   * Computed on every write (add/edit) so it always mirrors `itemsUsed`;
   * backfilled once for pre-Phase-8 creations.
   */
  itemsUsedRefs?: string[];

  // Creation type (what kind of creation is this?)
  creationType?: 'mani' | 'pedi' | 'nail-art' | 'practice' | 'press-ons' | 'jewelry' | 'other';

  // Optional details
  dateCreated?: string;
  techniques?: string[];
  difficulty?: 'beginner' | 'intermediate' | 'advanced';
  timeToComplete?: string;
  
  // Marketplace fields
  listedOnMarketplace?: boolean;
  marketplaceCategory?: 'pressons' | 'jewelry' | 'other';
  marketplaceStatus?: 'available' | 'pending' | 'sold';
  price?: number;
  contactForPricing?: boolean;
  openToTrades?: boolean;
  
  // Press-on specific
  pressOnDetails?: PressOnDetails;
  
  // Timestamps
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// ============================================================================
// CONSTANTS
// ============================================================================

// Art techniques for tagging creations
export const TECHNIQUES = [
  'Freehand',
  'Stamping',
  'Waterslide Decals',
  'Stickers/Nail Wraps',
  'Foil Transfer',
  'Airbrush',
  'Dip Powder',
  'Polygel',
  'Acrylic Sculpting',
  'Gel Extensions',
  'Builder Gel',
  'Glitter Placement',
  'Ombré/Gradient',
  'Water Marble',
  'Dry Marble',
  'Dotting',
  'Ink Art',
  'Watercolor',
  'Line Art',
  '3D Art',
  'Encapsulation',
  'Taping',
  'Reverse Stamping',
  'Leadlighting',
  'Jelly Sandwich'
] as const;

// Nail shapes for press-ons
export const NAIL_SHAPES = [
  'Round',
  'Square',
  'Squoval',
  'Oval',
  'Almond',
  'Coffin/Ballerina',
  'Stiletto',
  'Lipstick',
  'Duck/Flare',
  'Edge/Mountain Peak'
] as const;

// Nail lengths for press-ons
export const NAIL_LENGTHS = [
  'XS',
  'Short',
  'Medium',
  'Long',
  'XL',
  'XXL'
] as const;

// Creation types (what kind of creation is this?)
export const CREATION_TYPES = [
  { value: 'mani', label: '💅 Manicure', description: 'Regular manicure' },
  { value: 'pedi', label: '🦶 Pedicure', description: 'Pedicure creation' },
  { value: 'nail-art', label: '🎨 Nail Art', description: 'Artistic nail design' },
  { value: 'practice', label: '📝 Practice', description: 'Practice or swatch' },
  { value: 'press-ons', label: '✨ Press-On Set', description: 'Handmade press-on nails' },
  { value: 'jewelry', label: '💍 Jewelry', description: 'Nail polish jewelry' },
  { value: 'other', label: '📦 Other', description: 'Other creation' }
] as const;

// Marketplace categories
export const MARKETPLACE_CATEGORIES = [
  { value: 'pressons', label: '💅 Press-Ons', description: 'Ready-to-wear nail sets' },
  { value: 'jewelry', label: '💍 Nail Polish Jewelry', description: 'Charms, chains, gems' },
  { value: 'other', label: '✨ Other', description: 'Art, accessories, etc.' }
] as const;

// Difficulty levels
export const DIFFICULTY_LEVELS = [
  { value: 'beginner', label: 'Beginner', emoji: '🌱' },
  { value: 'intermediate', label: 'Intermediate', emoji: '🌿' },
  { value: 'advanced', label: 'Advanced', emoji: '🌳' }
] as const;

// Type exports for the constants
export type Technique = typeof TECHNIQUES[number];
export type NailShape = typeof NAIL_SHAPES[number];
export type NailLength = typeof NAIL_LENGTHS[number];
export type CreationType = typeof CREATION_TYPES[number]['value'];
export type MarketplaceCategory = typeof MARKETPLACE_CATEGORIES[number]['value'];
export type DifficultyLevel = typeof DIFFICULTY_LEVELS[number]['value'];
